import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, MessageCircle } from 'lucide-react';
import { chatAPI } from '../services/api';

const Chat = ({ user }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Check both user prop and localStorage to avoid race condition
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!user && !savedUser && !token) {
      navigate('/login');
      return;
    }
    
    loadMessages();
  }, [user, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      const data = await chatAPI.getMessages();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const data = await chatAPI.sendMessage(newMessage);
      setMessages([...messages, data.message]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  if (!user && !localStorage.getItem('user')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-20 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <MessageCircle className="h-10 w-10 text-red-800" />
            <h1 className="text-5xl font-black text-zinc-100">LIVE CHAT</h1>
          </div>
          <p className="text-zinc-400 text-lg">Chat with Steve about your piercing questions</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 overflow-hidden flex flex-col" style={{ height: '600px' }}>
          {/* Chat Header */}
          <div className="bg-red-900 p-4 border-b border-zinc-800">
            <h3 className="text-white font-bold">Steve - Professional Piercer</h3>
            <p className="text-red-200 text-sm">Usually replies within minutes</p>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {isLoading ? (
              <div className="text-center text-zinc-400">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="text-center text-zinc-400">No messages yet. Start a conversation!</div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg._id || msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-4 ${
                      msg.sender === 'user'
                        ? 'bg-red-800 text-white'
                        : 'bg-zinc-800 text-zinc-100'
                    }`}
                  >
                    <p className="leading-relaxed">{msg.message}</p>
                    <p className="text-xs mt-2 opacity-70">
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-zinc-800 bg-zinc-950">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-zinc-900 border-zinc-700 text-zinc-100 focus:border-red-800"
              />
              <Button
                type="submit"
                className="bg-red-800 hover:bg-red-700 text-white px-6"
              >
                <Send className="h-5 w-5" />
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-6 bg-zinc-900 border border-zinc-800 p-6">
          <h3 className="text-zinc-100 font-bold mb-3">Chat Guidelines</h3>
          <ul className="text-zinc-400 space-y-2 text-sm">
            <li>• This chat is for existing clients only</li>
            <li>• Response time is typically within a few hours</li>
            <li>• For emergencies, please contact your doctor</li>
            <li>• For booking appointments, use the booking form</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Chat;